import { Component, DestroyRef, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DxFormComponent } from 'devextreme-angular/ui/form';
import type { ValueChangedEvent } from 'devextreme/ui/select_box';
import { RouteKeys } from '../../helper/route-keys.helper';
import { ApiService } from '../../services/api.service';
import { ConfigService } from '../../services/config.service';
import { LanguageService } from '../../services/language.service';
import { Subject, Subscription, debounceTime } from 'rxjs';

interface CalculatorKonfiguration {
  gefunden: boolean;
  istEkAbhaengig: boolean;
  istZaehlkennzeichen: boolean;
  text: string;
}

interface CalculatorFormData {
  zeitraumAb?: Date | string | null;
  betreuungsArt?: number | null;
  betreuungsUmfang?: number | null;
  einrichtungId: number | null;
  einkommen: number | null;
  anzahlKinder: number | null;
  istMaxEinkommen: boolean;
  istAlleinerziehend: boolean;
}

interface CalculatorErgebnis {
  beitrag: number;
  text: string;
}

@Component({
  selector: 'app-calculator',
  templateUrl: './calculator.component.html',
  styleUrls: ['./calculator.component.scss'],
  standalone: false,
})
export class CalculatorComponent {
  readonly calcForm = viewChild<DxFormComponent>('calcForm');

  private readonly configService = inject(ConfigService);
  private readonly languageService = inject(LanguageService);
  private readonly apiService = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sprache = toSignal(this.languageService.get_selectedLanguageObservable, {
    initialValue: this.languageService.getLanguage(),
  });

  // DevExtreme aktualisiert formData direkt; deshalb bewusst kein Signal um dieses Objekt.
  formData: CalculatorFormData = this.createFormData();
  readonly inProcess = signal(false);
  readonly betrag = signal(0);
  readonly istBerechnet = signal(false);
  // Der letzte Ergebnisbereich bleibt für ein stabiles Layout bestehen,
  // auch wenn sein Betrag nach einer Eingabe nicht mehr aktuell ist.
  readonly ergebnisVorhanden = signal(false);
  readonly eingabeWirdVerarbeitet = signal(false);
  readonly konfiguration = signal<CalculatorKonfiguration | null>(null);
  readonly konfigurationLaedt = signal(false);
  private readonly meldung = signal<{ text: string } | { key: string }>({ text: '' });

  readonly istEKAbhaengig = this.configService.getGlobalSetting('brEK_Abhaengig') === '1';
  private readonly einkommensArt = Number(this.configService.getGlobalSetting('brEinkommensArt'));
  readonly einrichtungenBeruecksichtigen =
    this.configService.getGlobalSetting('brEinrichtungenBeruecksichtigen') === '1';
  readonly einrichtungenDataSource = this.einrichtungenBeruecksichtigen ? this.configService.getEinrichtungen() : [];
  readonly betreuungsArtDataSource = this.configService.getKatalogMitKeineAntragstellung('BetreuungsArtKatalog');
  readonly betreuungsUmfangDataSource = signal<any[]>([]);

  readonly hinweisText = computed(() => {
    const sprachCode = (this.sprache() || 'de').slice(0, 2).toUpperCase();
    return this.configService.getGlobalSetting('brBemerkung' + sprachCode) ?? '';
  });

  readonly einkommenText = computed(() => {
    this.sprache(); // Die Übersetzungen des bestehenden Sprachdienstes reagieren auf diesen Wechsel.
    const keys = ['annualGrossIncome', 'annualNetIncome', 'mtlGrossIncome', 'mtlNetIncome'];
    return this.languageService.translate(keys[this.einkommensArt] ?? keys[0]);
  });

  readonly kind_format = computed(() => {
    this.sprache();
    return "#'.' " + this.languageService.translate('positionOfKid_2');
  });

  readonly msg = computed(() => {
    this.sprache();
    const meldung = this.meldung();
    return 'key' in meldung ? this.languageService.translate(meldung.key) : meldung.text;
  });

  readonly istZaehl_KZ = computed(() => this.konfiguration()?.istZaehlkennzeichen === true);
  readonly kannBerechnen = computed(() => {
    const konfiguration = this.konfiguration();
    return konfiguration?.gefunden === true && !this.konfigurationLaedt() && !this.eingabeWirdVerarbeitet() &&
      (!konfiguration.istEkAbhaengig || this.istEKAbhaengig);
  });

  private readonly feldAenderungen = new Subject<void>();
  private konfigurationSubscription?: Subscription;
  private berechnungSubscription?: Subscription;
  private kontextKey = '';
  private automatischBerechnen = false;

  constructor() {
    // Schnelle Eingaben bündeln; laufende Anfragen werden trotzdem sofort abgebrochen.
    this.feldAenderungen.pipe(
      debounceTime(250),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.eingabeWirdVerarbeitet()) return;
      this.eingabeWirdVerarbeitet.set(false);
      this.aktualisiereKonfiguration();
    });
    this.destroyRef.onDestroy(() => this.feldAenderungen.complete());
  }

  onFormFieldChanged(): void {
    this.automatischBerechnen ||= this.istBerechnet() || this.inProcess();
    this.eingabeWirdVerarbeitet.set(true);
    this.berechnungSubscription?.unsubscribe();
    this.konfigurationSubscription?.unsubscribe();
    // Ein abgebrochener Konfigurationsabruf muss auch bei unverändertem Kontext neu starten.
    if (this.konfigurationLaedt()) this.kontextKey = '';
    this.inProcess.set(false);
    this.istBerechnet.set(false);
    this.feldAenderungen.next();
  }

  private aktualisiereKonfiguration(): void {
    const request = {
      zeitraumAb: this.formData.zeitraumAb,
      betreuungsArt: this.formData.betreuungsArt,
      betreuungsUmfang: this.formData.betreuungsUmfang,
      einrichtungId: this.einrichtungenBeruecksichtigen ? this.formData.einrichtungId : null,
    };
    const key = JSON.stringify(request);
    if (key === this.kontextKey) {
      if (this.automatischBerechnen && this.kannBerechnen()) this.onCalcClick();
      return;
    }

    this.kontextKey = key;
    this.konfiguration.set(null);
    this.konfigurationLaedt.set(false);
    this.meldung.set({ text: '' });
    if (!request.zeitraumAb || !request.betreuungsArt || !request.betreuungsUmfang ||
        (this.einrichtungenBeruecksichtigen && !request.einrichtungId)) return;

    this.konfigurationLaedt.set(true);
    this.konfigurationSubscription = this.apiService.post<CalculatorKonfiguration>(
      RouteKeys.Calculator.get_konfiguration, request,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.konfigurationLaedt.set(false);
        this.konfiguration.set(response);
        this.meldung.set(response.gefunden && response.istEkAbhaengig && !this.istEKAbhaengig
          ? { key: 'calculatorIncomeHidden' }
          : { text: response.text || '' });
        if (this.automatischBerechnen && this.kannBerechnen()) this.onCalcClick();
      },
      error: () => {
        this.konfigurationLaedt.set(false);
        this.kontextKey = '';
        this.meldung.set({ key: 'serverError' });
      },
    });
  }

  readonly onCalcClick = (): void => {
    const form = this.calcForm();
    if (!this.kannBerechnen() || this.inProcess() || !form?.instance.validate().isValid) return;
    this.inProcess.set(true);
    this.meldung.set({ text: '' });
    const request = {
      ...this.formData,
      einrichtungId: this.einrichtungenBeruecksichtigen ? this.formData.einrichtungId : null,
      einkommen: this.istEKAbhaengig ? this.formData.einkommen : null,
      istMaxEinkommen: this.istEKAbhaengig && this.formData.istMaxEinkommen,
      // Das bestehende Feld liefert Kinderzahl ODER Zählkennzeichen, entsprechend der Konfiguration.
      anzahlKinder: this.formData.anzahlKinder,
    };
    this.berechnungSubscription = this.apiService.post<CalculatorErgebnis>(
      RouteKeys.Calculator.get_beitrag, request,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.betrag.set(response?.beitrag ?? -1);
        this.meldung.set({ text: response?.text ?? '' });
        this.istBerechnet.set(true);
        this.ergebnisVorhanden.set(true);
        this.inProcess.set(false);
        this.automatischBerechnen = true;
      },
      error: () => {
        this.inProcess.set(false);
        this.meldung.set({ key: 'serverError' });
      },
    });
  };

  readonly onClickReset = (): void => {
    this.automatischBerechnen = false;
    this.berechnungSubscription?.unsubscribe();
    this.konfigurationSubscription?.unsubscribe();
    this.inProcess.set(false);
    this.istBerechnet.set(false);
    this.ergebnisVorhanden.set(false);
    this.eingabeWirdVerarbeitet.set(false);
    this.betrag.set(0);
    this.meldung.set({ text: '' });
    this.kontextKey = '';
    this.konfiguration.set(null);
    this.konfigurationLaedt.set(false);
    this.betreuungsUmfangDataSource.set([]);
    this.formData = this.createFormData(0);
  };

  readonly onBetreuungsArtValueChanged = (e: ValueChangedEvent): void => {
    const umfang = this.configService.getKatalog('BetreuungsUmfangKatalog')
      .filter((x) => x.beziehungen.includes(e.value));
    this.betreuungsUmfangDataSource.set(umfang);
    if (!umfang.some((x) => x.id === this.formData.betreuungsUmfang)) {
      this.calcForm()?.instance.updateData('betreuungsUmfang', null);
    }
  };

  private createFormData(einkommen: number | null = null): CalculatorFormData {
    return { istMaxEinkommen: false, istAlleinerziehend: false, einkommen, anzahlKinder: 1, einrichtungId: null };
  }
}
